import React, { useState, useEffect } from "react";
import {
  Menu as MenuIcon,
  ArrowDropDownOutlined,
  LightModeOutlined,
  DarkModeOutlined,
  PersonOutlined,
  AccountBalanceWalletOutlined,
  LockOutlined,
  LogoutOutlined,
} from "@mui/icons-material";
import FlexBetween from "components/FlexBetween";
import {
  AppBar,
  Button,
  Box,
  Typography,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setMode } from "state";
import { logout } from "utils";
import ClientChangePasswordDialog from "./ClientChangePasswordDialog";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";

const readCustomer = () => {
  try {
    return JSON.parse(localStorage.getItem("customer") || "{}");
  } catch {
    return {};
  }
};

const NavbarCustomer = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const [customer, setCustomer] = useState(readCustomer);

  useEffect(() => {
    const refresh = () => setCustomer(readCustomer());
    window.addEventListener("customer-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("customer-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const customerName = customer?.name || "Customer";
  const firstLetter = customerName.charAt(0).toUpperCase();
  const profilePictureSrc = customer?.profilePicture
    ? `/uploads/profiles/${customer.profilePicture}?v=${customer._updatedAt || customer.profilePicture}`
    : null;

  const frameSx = {
    height: "38px",
    borderRadius: "10px",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": { borderColor: theme.palette.secondary.light },
  };

  return (
    <AppBar
      sx={{
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        background: theme.palette.background.default,
        boxShadow: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: "16px" }}>
        {/* LEFT — sidebar toggle */}
        <IconButton
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          sx={{
            ...frameSx,
            width: "38px",
            flex: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "3.5px",
          }}
        >
          <Box component="span" sx={{ width: "16px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark }} />
          <Box component="span" sx={{ width: "11px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark, alignSelf: "flex-start", ml: "9px" }} />
          <Box component="span" sx={{ width: "16px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark }} />
        </IconButton>

        {/* RIGHT — dark mode + language + notifications + avatar */}
        <FlexBetween gap="0.5rem" sx={{ flex: "none" }}>
          <IconButton onClick={() => dispatch(setMode())} sx={{ ...frameSx, width: "38px" }}>
            {theme.palette.mode === "dark"
              ? <DarkModeOutlined sx={{ fontSize: "20px" }} />
              : <LightModeOutlined sx={{ fontSize: "20px" }} />}
          </IconButton>

          <Box sx={{ ...frameSx, display: "flex", alignItems: "center", px: "4px" }}>
            <LanguageSwitcher />
          </Box>
          <Box sx={{ ...frameSx, width: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <NotificationBell />
          </Box>

          <FlexBetween>
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                ...frameSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textTransform: "none",
                gap: "0.75rem",
                pl: "14px",
                pr: "5px",
                height: "44px",
              }}
            >
              <Box textAlign="left" sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography fontWeight="bold" fontSize="0.8125rem" lineHeight={1.2} sx={{ color: theme.palette.text.primary }} noWrap>
                  {customerName}
                </Typography>
                <Typography fontSize="0.65625rem" sx={{ color: theme.palette.secondary[300] }}>
                  Customer
                </Typography>
              </Box>

              <Avatar
                src={profilePictureSrc || undefined}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "9px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  bgcolor: profilePictureSrc ? "transparent" : theme.palette.secondary.main,
                  color: "white",
                }}
              >
                {!profilePictureSrc && firstLetter}
              </Avatar>

              <ArrowDropDownOutlined
                sx={{
                  color: theme.palette.secondary[300],
                  fontSize: "20px",
                  mr: "4px",
                  transition: "transform 0.2s",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Button>

            {/* Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                elevation: 3,
                sx: { borderRadius: 2, minWidth: 200, mt: 0.5 },
              }}
            >
              {/* Profile header inside menu */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography fontWeight={600} fontSize="0.9rem" noWrap>
                  {customerName}
                </Typography>
                {customer?.whatsapp && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {customer.whatsapp}
                  </Typography>
                )}
              </Box>

              <MenuItem
                onClick={() => { setAnchorEl(null); navigate("/myprofile"); }}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon><PersonOutlined fontSize="small" /></ListItemIcon>
                <ListItemText>My Profile</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => { setAnchorEl(null); navigate("/wallet"); }}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon><AccountBalanceWalletOutlined fontSize="small" /></ListItemIcon>
                <ListItemText>My Wallet</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={() => { setAnchorEl(null); setChangePasswordOpen(true); }}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon><LockOutlined fontSize="small" /></ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => logout()}
                sx={{ py: 1.25, color: theme.palette.error.main }}
              >
                <ListItemIcon>
                  <LogoutOutlined fontSize="small" sx={{ color: theme.palette.error.main }} />
                </ListItemIcon>
                <ListItemText>Log Out</ListItemText>
              </MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>

      <ClientChangePasswordDialog open={changePasswordOpen} setOpen={setChangePasswordOpen} />
    </AppBar>
  );
};

export default NavbarCustomer;
